import { useCallback, useEffect } from "react";
import classNames from "classnames/bind";

import { QuizAnswerOption, QuizConfiguration } from "../../gql/graphql";
import { CustomizedBackground } from "../CustomizedBackground";
import { QuizSummary } from "./QuizSummary";
import { DemographicQuestions } from "./DemographicQuestions";

import { QuizQuestion } from "./QuizQuestion";
import { useGlobalConfiguration } from "../../hooks/useGlobalConfiugration";
import { GameHandlerProps } from "../../types";
import { useQuizState, QuizGameState } from "./hooks/useQuizState";
import { useQuizSession } from "./hooks/useQuizSession";
import { useQuizCompletion } from "./hooks/useQuizCompletion";
import MyEoELife from "../../assets/images/my-eoe-life.png";

import styles from "./QuizGame.module.scss";

const cx = classNames.bind(styles);

export const QuizGame = ({ game }: GameHandlerProps) => {
  const configuration = game.configuration as QuizConfiguration;
  const { questions, gameVisuals } = configuration;
  const { isPreviewMode } = useGlobalConfiguration();

  // Google Tag Manager
  useEffect(() => {
    // Inject GTM script into head
    const gtmScript = document.createElement("script");
    gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-5RJCZT2J');`;
    document.head.appendChild(gtmScript);

    // Inject GTM noscript iframe into body
    const gtmNoscript = document.createElement("noscript");
    gtmNoscript.innerHTML =
      '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-5RJCZT2J" height="0" width="0" style="display:none;visibility:hidden"></iframe>';
    document.body.insertBefore(gtmNoscript, document.body.firstChild);

    // Cleanup function to remove GTM when component unmounts
    return () => {
      const scripts = document.head.querySelectorAll("script");
      scripts.forEach((script) => {
        if (script.innerHTML.includes("GTM-5RJCZT2J")) {
          script.remove();
        }
      });

      const noscripts = document.body.querySelectorAll("noscript");
      noscripts.forEach((noscript) => {
        if (noscript.innerHTML.includes("GTM-5RJCZT2J")) {
          noscript.remove();
        }
      });
    };
  }, []);

  const quizState = useQuizState(questions.length);

  const quizSession = useQuizSession({
    configurationId: configuration.id,
    isPreviewMode,
  });

  const quizCompletion = useQuizCompletion({
    gameId: game.id,
    questions,
    isPreviewMode,
    quizSessionId: quizSession.quizSessionId,
    startTime: quizSession.startTime,
    completeSession: quizSession.completeSession,
  });

  // Auto-initialize session when component mounts
  useEffect(() => {
    const initializeSession = async () => {
      try {
        await quizSession.initializeSession();
      } catch (error) {
        console.warn("Failed to initialize session:", error);
      }
    };

    initializeSession();
  }, [quizSession]);

  const handleAnswerSelect = useCallback(
    async (selectedOption: QuizAnswerOption) => {
      if (quizState.isSubmitting) return;

      quizState.selectAnswer(selectedOption);

      if (quizSession.quizSessionId) {
        await quizSession.updateSession(
          quizSession.quizSessionId,
          quizState.currentQuestionIndex,
          questions[quizState.currentQuestionIndex],
          selectedOption,
        );
      }
    },
    [quizState, quizSession, questions],
  );

  const handleDemographicsComplete = useCallback(async () => {
    quizState.completeDemographics();

    try {
      await quizCompletion.handleQuizComplete(quizState.selectedAnswers);
    } catch (error) {
      console.error("Error completing quiz:", error);
    }
  }, [quizState, quizCompletion]);

  switch (quizState.gameState) {
    case QuizGameState.DEMOGRAPHICS:
      return (
        <CustomizedBackground config={gameVisuals} className={cx("sectionContainer")}>
          <img src={MyEoELife} alt="My EoE Life" className={cx("logo")} />
          <div
            className={cx("quizGame")}
            style={
              {
                "--theme-color": gameVisuals.themeColor,
                "--theme-color-light": `${gameVisuals.themeColor}20`,
                "--background-color": gameVisuals.backgroundColor,
              } as React.CSSProperties
            }
          >
            <DemographicQuestions
              sessionId={quizSession.quizSessionId}
              onComplete={handleDemographicsComplete}
              gameVisuals={gameVisuals}
              isPreviewMode={isPreviewMode}
            />
          </div>
        </CustomizedBackground>
      );

    case QuizGameState.SUMMARY:
      return (
        <CustomizedBackground config={gameVisuals} className={cx("sectionContainer", "centered")}>
          <img src={MyEoELife} alt="My EoE Life" className={cx("logo")} />
          <QuizSummary
            totalPoints={quizState.totalPoints}
            gameVisuals={gameVisuals}
            answers={quizState.selectedAnswers}
          />
        </CustomizedBackground>
      );

    case QuizGameState.PLAYING:
    default:
      return (
        <CustomizedBackground config={gameVisuals} className={cx("sectionContainer")}>
          <img src={MyEoELife} alt="My EoE Life" className={cx("logo")} />
          <div
            className={cx("quizGame")}
            style={
              {
                "--theme-color": gameVisuals.themeColor,
                "--theme-color-light": `${gameVisuals.themeColor}20`,
                "--background-color": gameVisuals.backgroundColor,
              } as React.CSSProperties
            }
          >
            <QuizQuestion
              question={questions[quizState.currentQuestionIndex]}
              currentQuestionIndex={quizState.currentQuestionIndex}
              totalQuestions={questions.length}
              gameVisuals={gameVisuals}
              isSubmitting={quizState.isSubmitting}
              onAnswerSelect={handleAnswerSelect}
            />
          </div>
        </CustomizedBackground>
      );
  }
};
